<script setup lang="ts">
// Name the component so <KeepAlive include="SessionList"> in App.vue can
// target it — Vue uses this name to decide which component instances to
// cache when the route swaps. With this, navigating to /replay/:id and
// back restores filter inputs, page, scroll position, etc.
defineOptions({ name: 'SessionList' })

import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useRouter } from 'vue-router'
import { apiFetch } from '@/utils/api'
import ConfirmDialog from '@/components/ConfirmDialog.vue'

// Types
interface Session {
  sessionId: string
  pageUrl?: string
  startTime?: string
  durationMs?: number
  eventCount?: number
  status?: string
  userId?: string
  errorCount?: number
  hasErrors?: boolean
  frustrationScore?: number
  sizeBytes?: number
  favoritedByMe?: boolean
  deviceType?: string | null
  country?: string | null
  countryName?: string | null
  region?: string | null
  city?: string | null
}

interface SessionsResponse {
  items?: Session[]
  totalCount?: number
  totalPages?: number
  page?: number
  pageSize?: number
}

type SortColumn = 'sessionId' | 'userId' | 'pageUrl' | 'startTime' | 'durationMs' | 'eventCount' | 'status' | 'sizeBytes'
type SortDirection = 'asc' | 'desc'

// Router
const router = useRouter()

// State
const sessions = ref<Session[]>([])
const searchQuery = ref('')
const loading = ref(false)
const fetchError = ref<string | null>(null)
const currentPage = ref(1)
const pageSize = ref(10)
const statusFilter = ref('')
const userFilter = ref('')
const urlFilter = ref('')
const hasErrorsFilter = ref('')
const minRequestDurationFilter = ref('')
const errorContainsFilter = ref('')
const customEventFilter = ref('')
const favoritesOnly = ref(false)

// Error picker — paginated list of distinct error fingerprints aggregated
// across sessions; click a row to set errorContainsFilter and close.
interface ErrorOption { fingerprint: string; sessionCount: number }
const errorPickerOpen = ref(false)
const errorPickerLoading = ref(false)
const errorPickerError = ref<string | null>(null)
const errorPickerItems = ref<ErrorOption[]>([])
const errorPickerTotal = ref(0)
const errorPickerPage = ref(1)
const errorPickerPageSize = 20
const errorPickerQuery = ref('')

async function loadErrorPickerPage() {
  errorPickerLoading.value = true
  errorPickerError.value = null
  try {
    const params = new URLSearchParams()
    params.set('page', String(errorPickerPage.value))
    params.set('pageSize', String(errorPickerPageSize))
    if (errorPickerQuery.value.trim()) params.set('q', errorPickerQuery.value.trim())
    if (projectFilter.value) params.set('projectId', projectFilter.value)
    const resp = await apiFetch(`/sessions/error-options?${params}`)
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
    const data = await resp.json()
    errorPickerItems.value = data.items ?? []
    errorPickerTotal.value = data.totalCount ?? 0
  } catch (e) {
    errorPickerError.value = e instanceof Error ? e.message : String(e)
  } finally {
    errorPickerLoading.value = false
  }
}

function openErrorPicker() {
  errorPickerOpen.value = true
  errorPickerPage.value = 1
  errorPickerQuery.value = ''
  loadErrorPickerPage()
}

function pickError(fingerprint: string) {
  errorContainsFilter.value = fingerprint
  errorPickerOpen.value = false
  filterSessions()
}

function changeErrorPickerPage(delta: number) {
  const max = Math.max(1, Math.ceil(errorPickerTotal.value / errorPickerPageSize))
  errorPickerPage.value = Math.min(max, Math.max(1, errorPickerPage.value + delta))
  loadErrorPickerPage()
}

// Custom-event tag picker — same pattern as the error picker, aggregated
// over CustomEventTags; click a row to set customEventFilter and close.
interface TagOption { tag: string; sessionCount: number }
const tagPickerOpen = ref(false)
const tagPickerLoading = ref(false)
const tagPickerError = ref<string | null>(null)
const tagPickerItems = ref<TagOption[]>([])
const tagPickerTotal = ref(0)
const tagPickerPage = ref(1)
const tagPickerPageSize = 20
const tagPickerQuery = ref('')

async function loadTagPickerPage() {
  tagPickerLoading.value = true
  tagPickerError.value = null
  try {
    const params = new URLSearchParams()
    params.set('page', String(tagPickerPage.value))
    params.set('pageSize', String(tagPickerPageSize))
    if (tagPickerQuery.value.trim()) params.set('q', tagPickerQuery.value.trim())
    if (projectFilter.value) params.set('projectId', projectFilter.value)
    const resp = await apiFetch(`/sessions/custom-tag-options?${params}`)
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
    const data = await resp.json()
    tagPickerItems.value = data.items ?? []
    tagPickerTotal.value = data.totalCount ?? 0
  } catch (e) {
    tagPickerError.value = e instanceof Error ? e.message : String(e)
  } finally {
    tagPickerLoading.value = false
  }
}

function openTagPicker() {
  tagPickerOpen.value = true
  tagPickerPage.value = 1
  tagPickerQuery.value = ''
  loadTagPickerPage()
}

function pickTag(tag: string) {
  customEventFilter.value = tag
  tagPickerOpen.value = false
  filterSessions()
}

function changeTagPickerPage(delta: number) {
  const max = Math.max(1, Math.ceil(tagPickerTotal.value / tagPickerPageSize))
  tagPickerPage.value = Math.min(max, Math.max(1, tagPickerPage.value + delta))
  loadTagPickerPage()
}

// Filter autocomplete options (populated from /api/sessions/filter-options)
const userOptions = ref<string[]>([])
const urlOptions = ref<string[]>([])

/**
 * Distinct hosts derived from urlOptions. The backend returns every
 * distinct pageUrl it's seen; that produces 20+ entries per tenant
 * (root, /login, /DesktopClient/Index, /admin/…) which is unusable
 * as a filter dropdown. Rolling up to host gives one entry per
 * deployment (deltavorm.sphprd.com, holstra.sphprd.com, …).
 *
 * Filter still submits the host string as ?url=…; server does a
 * substring match on pageUrl so it correctly finds every session
 * whose URL starts with that host.
 */
const hostOptions = computed<string[]>(() => {
  const seen = new Set<string>()
  for (const u of urlOptions.value) {
    const h = hostFromUrl(u)
    if (h && h !== '-') seen.add(h)
  }
  return Array.from(seen).sort()
})
const projectFilter = ref('')
const projectList = ref<{id: string, name: string}[]>([])
const serverTotalPages = ref(0)
const serverTotalCount = ref(0)
const sortColumn = ref<SortColumn>('startTime')
const sortDirection = ref<SortDirection>('desc')
const copiedId = ref<string | null>(null)

// Formatters
const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString()
}

const formatRelativeTime = (dateString: string | undefined): string => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
}

const formatDuration = (ms: number | undefined): string => {
  if (!ms) return '-'
  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ${seconds % 60}s`
}

/**
 * Extract the host from a full URL for display in the compact table
 * cell. Full URL is still shown on hover via the cell's :title. Falls
 * back to the raw string when it isn't parseable (e.g. sessions
 * captured on a page with a chrome-extension:// or file:// origin).
 * 'www.' prefix stripped — visual noise, no signal.
 */
const hostFromUrl = (url: string | undefined): string => {
  if (!url) return '-'
  try {
    const h = new URL(url).host
    return h.startsWith('www.') ? h.slice(4) : h
  } catch {
    return url
  }
}

/**
 * Deterministic avatar per user — same userId always renders the same robot.
 * Encoded so userIds containing slashes (e.g. "users/admin") survive the URL.
 */
const avatarUrl = (userId: string): string =>
  `https://api.dicebear.com/10.x/bottts/svg?seed=${encodeURIComponent(userId)}`

const deviceIcon = (deviceType: string | null | undefined): string => {
  switch (deviceType) {
    case 'mobile': return '📱'
    case 'tablet': return '📱'
    case 'bot':    return '🤖'
    case 'desktop': return '🖥️'
    default: return '❔'
  }
}

/**
 * Render a 2-letter country code as the regional-indicator flag emoji.
 * "US" → 🇺🇸. Falls back to empty when the code isn't 2 letters.
 */
const countryFlag = (cc: string | null | undefined): string => {
  if (!cc || cc.length !== 2) return ''
  const A = 0x1F1E6
  return String.fromCodePoint(A + cc.charCodeAt(0) - 65, A + cc.charCodeAt(1) - 65)
}

const locationText = (s: Session): string => {
  const parts = [s.city, s.region, s.countryName ?? s.country].filter(Boolean)
  return parts.join(', ')
}

const formatBytes = (bytes: number | undefined | null): string => {
  if (bytes == null || bytes === 0) return '-'
  const mb = bytes / (1024 * 1024)
  if (mb >= 1) return `${mb.toFixed(2)} MB`
  const kb = bytes / 1024
  return `${kb.toFixed(1)} KB`
}

const shortenId = (id: string | undefined): string => {
  if (!id) return '-'
  return id.length > 20 ? `${id.substring(0, 8)}...${id.substring(id.length - 8)}` : id
}

// Copy to clipboard
const copyToClipboard = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text)
    copiedId.value = text
    setTimeout(() => {
      copiedId.value = null
    }, 2000)
  } catch (error) {
    console.error('Failed to copy:', error)
  }
}

// Filtered sessions — derived reactively from sessions + searchQuery
const filteredSessions = computed(() => {
  if (!searchQuery.value) return sessions.value
  const query = searchQuery.value.toLowerCase()
  return sessions.value.filter(session =>
    session.sessionId?.toLowerCase().includes(query) ||
    session.pageUrl?.toLowerCase().includes(query)
  )
})

// Sorting
const sortSessions = (column: SortColumn): void => {
  if (sortColumn.value === column) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortColumn.value = column
    sortDirection.value = 'desc'
  }
}

const sortedSessions = computed(() => {
  const sorted = [...filteredSessions.value]
  sorted.sort((a, b) => {
    const aVal = a[sortColumn.value]
    const bVal = b[sortColumn.value]

    if (aVal === undefined || aVal === null) return 1
    if (bVal === undefined || bVal === null) return -1

    let comparison = 0
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      comparison = aVal.localeCompare(bVal)
    } else if (typeof aVal === 'number' && typeof bVal === 'number') {
      comparison = aVal - bVal
    }

    return sortDirection.value === 'asc' ? comparison : -comparison
  })
  return sorted
})

// Pagination — server handles paging, so totalPages comes from the server response
const totalPages = computed(() => serverTotalPages.value)

// No client-side slicing needed — server already returns the correct page
const paginatedSessions = computed(() => sortedSessions.value)

const goToPage = (page: number): void => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
    fetchSessions()
  }
}

const pageNumbers = computed(() => {
  const pages: (number | string)[] = []
  const total = totalPages.value

  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i)
    }
  } else {
    if (currentPage.value <= 3) {
      for (let i = 1; i <= 4; i++) pages.push(i)
      pages.push('...')
      pages.push(total)
    } else if (currentPage.value >= total - 2) {
      pages.push(1)
      pages.push('...')
      for (let i = total - 3; i <= total; i++) pages.push(i)
    } else {
      pages.push(1)
      pages.push('...')
      pages.push(currentPage.value - 1)
      pages.push(currentPage.value)
      pages.push(currentPage.value + 1)
      pages.push('...')
      pages.push(total)
    }
  }

  return pages
})

// API calls
const fetchSessions = async (): Promise<void> => {
  try {
    loading.value = true
    fetchError.value = null

    const params = new URLSearchParams()
    params.set('page', String(currentPage.value))
    params.set('pageSize', String(pageSize.value))
    if (statusFilter.value) {
      params.set('status', statusFilter.value)
    }
    if (userFilter.value) {
      params.set('userId', userFilter.value)
    }
    if (urlFilter.value) {
      params.set('url', urlFilter.value)
    }
    if (hasErrorsFilter.value) {
      params.set('hasErrors', hasErrorsFilter.value)
    }
    if (errorContainsFilter.value.trim()) {
      params.set('errorContains', errorContainsFilter.value.trim())
    }
    if (customEventFilter.value.trim()) {
      params.set('customEventTag', customEventFilter.value.trim())
    }
    if (minRequestDurationFilter.value) {
      params.set('minRequestDurationMs', minRequestDurationFilter.value)
    }
    if (favoritesOnly.value) {
      params.set('favoritesOnly', 'true')
    }
    if (projectFilter.value) {
      params.set('projectId', projectFilter.value)
    }

    const response = await apiFetch(`/sessions?${params}`)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    const data: Session[] | SessionsResponse = await response.json()
    if (Array.isArray(data)) {
      sessions.value = data
      serverTotalCount.value = data.length
      serverTotalPages.value = 1
    } else {
      sessions.value = data.items || []
      serverTotalCount.value = data.totalCount ?? sessions.value.length
      serverTotalPages.value = data.totalPages ?? 1
    }
  } catch (error) {
    fetchError.value = error instanceof Error ? error.message : 'Failed to fetch sessions'
  } finally {
    loading.value = false
  }
}

const refreshSessions = (): void => {
  fetchSessions()
}

const filterSessions = (): void => {
  // Reset to first page and re-fetch from server
  currentPage.value = 1
  fetchSessions()
}

const selectSession = (session: Session): void => {
  router.push(`/replay/${session.sessionId}`)
}

const toggleSessionFavorite = async (session: Session): Promise<void> => {
  try {
    const response = await apiFetch(`/sessions/${session.sessionId}/favorite`, { method: 'POST' })
    if (response.ok) {
      const data = await response.json()
      session.favoritedByMe = !!data.favorited
      // If the user is filtering to favorites only and just unstarred, hide it.
      if (favoritesOnly.value && !session.favoritedByMe) {
        sessions.value = sessions.value.filter(s => s.sessionId !== session.sessionId)
      }
    }
  } catch (err) {
    fetchError.value = `Failed to update favorite: ${err instanceof Error ? err.message : String(err)}`
  }
}

const deletingId = ref<string | null>(null)
const pendingDelete = ref<Session | null>(null)

const confirmDelete = (session: Session): void => {
  pendingDelete.value = session
}

const cancelDelete = (): void => {
  if (deletingId.value) return
  pendingDelete.value = null
}

const performDelete = async (): Promise<void> => {
  const session = pendingDelete.value
  if (!session) return

  deletingId.value = session.sessionId
  try {
    const response = await apiFetch(`/sessions/${session.sessionId}`, { method: 'DELETE' })
    if (response.ok || response.status === 204) {
      sessions.value = sessions.value.filter(s => s.sessionId !== session.sessionId)
      serverTotalCount.value = Math.max(0, serverTotalCount.value - 1)
    } else {
      const body = await response.text()
      fetchError.value = `Failed to delete session (${response.status}): ${body}`
    }
  } catch (err) {
    fetchError.value = `Failed to delete session: ${err instanceof Error ? err.message : String(err)}`
  } finally {
    deletingId.value = null
    pendingDelete.value = null
  }
}

const fetchProjects = async () => {
  try {
    const response = await apiFetch('/projects')
    if (response.ok) {
      projectList.value = await response.json()
    }
  } catch { /* projects endpoint may require admin */ }
}

const fetchFilterOptions = async () => {
  try {
    const response = await apiFetch('/sessions/filter-options')
    if (response.ok) {
      const data = await response.json()
      userOptions.value = data.users ?? []
      urlOptions.value = data.urls ?? []
    }
  } catch { /* non-critical — inputs still work without autocomplete */ }
}

// Lifecycle
const route = useRoute()

onMounted(() => {
  // Seed filters from URL query so deep-links like /sessions?userId=alice
  // (e.g. from the analytics Top-users panel) land pre-filtered.
  const qUser = route.query.userId
  if (typeof qUser === 'string' && qUser) userFilter.value = qUser
  const qUrl = route.query.url
  if (typeof qUrl === 'string' && qUrl) urlFilter.value = qUrl
  const qStatus = route.query.status
  if (typeof qStatus === 'string' && qStatus) statusFilter.value = qStatus
  const qError = route.query.errorContains
  if (typeof qError === 'string' && qError) errorContainsFilter.value = qError
  const qCustom = route.query.customEventTag
  if (typeof qCustom === 'string' && qCustom) customEventFilter.value = qCustom

  fetchSessions()
  fetchProjects()
  fetchFilterOptions()
})
</script>

<template>
  <div class="px-6" data-testid="session-list">
    <div class="flex items-center justify-end mb-4">
      <div class="flex items-center gap-2">
        <select
          v-model="userFilter"
          class="select select-bordered select-sm w-40"
          data-testid="user-filter"
          aria-label="Filter by user"
          @change="filterSessions"
        >
          <option value="">All Users</option>
          <option v-for="u in userOptions" :key="u" :value="u">{{ u }}</option>
        </select>
        <select
          v-model="urlFilter"
          class="select select-bordered select-sm w-56 max-w-xs"
          data-testid="url-filter"
          aria-label="Filter by URL"
          @change="filterSessions"
        >
          <option value="">All URLs</option>
          <option v-for="h in hostOptions" :key="h" :value="h" :title="h">{{ h }}</option>
        </select>
        <select
          v-model="statusFilter"
          class="select select-bordered select-sm"
          data-testid="status-filter"
          aria-label="Filter by status"
          @change="filterSessions"
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="abandoned">Abandoned</option>
        </select>
        <button
          class="btn btn-sm btn-square"
          :class="hasErrorsFilter === 'true' ? 'btn-error' : 'btn-ghost'"
          :title="hasErrorsFilter === 'true' ? 'Showing sessions with errors only' : 'Show sessions with errors only'"
          data-testid="errors-only-toggle"
          aria-label="Filter by errors"
          @click="hasErrorsFilter = hasErrorsFilter === 'true' ? '' : 'true'; filterSessions()"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4"
            :fill="hasErrorsFilter === 'true' ? 'currentColor' : 'none'"
            viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </button>
        <input
          v-model="errorContainsFilter"
          type="text"
          class="input input-bordered input-sm w-48"
          placeholder="Error contains… (TypeError, 500, /api/orders)"
          data-testid="error-contains-filter"
          aria-label="Filter by error content"
          maxlength="200"
          @change="filterSessions"
          @keydown.enter="filterSessions"
        />
        <button
          type="button"
          class="btn btn-sm btn-ghost border border-base-300"
          data-testid="browse-errors-button"
          title="Browse known errors — pick one to filter"
          @click="openErrorPicker"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1"
            viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
          </svg>
          <span class="text-xs">Browse</span>
        </button>
        <input
          v-model="customEventFilter"
          type="text"
          class="input input-bordered input-sm w-48"
          placeholder="Custom event… (order_submitted)"
          data-testid="custom-event-filter"
          aria-label="Filter by custom event tag"
          maxlength="200"
          @change="filterSessions"
          @keydown.enter="filterSessions"
        />
        <button
          type="button"
          class="btn btn-sm btn-ghost border border-base-300"
          data-testid="browse-custom-events-button"
          title="Browse custom event tags — pick one to filter"
          @click="openTagPicker"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1"
            viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
          </svg>
          <span class="text-xs">Browse</span>
        </button>
        <select
          v-model="minRequestDurationFilter"
          class="select select-bordered select-sm"
          data-testid="slow-filter"
          aria-label="Filter by slow requests"
          @change="filterSessions"
        >
          <option value="">Any speed</option>
          <option value="500">Slow ≥ 500ms</option>
          <option value="1000">Slow ≥ 1s</option>
          <option value="2000">Slow ≥ 2s</option>
          <option value="5000">Slow ≥ 5s</option>
        </select>
        <button
          class="btn btn-sm btn-square"
          :class="favoritesOnly ? 'btn-warning' : 'btn-ghost'"
          :title="favoritesOnly ? 'Showing favorites only' : 'Show favorites only'"
          data-testid="favorites-only-toggle"
          @click="favoritesOnly = !favoritesOnly; filterSessions()"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4"
            :fill="favoritesOnly ? 'currentColor' : 'none'"
            viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
        </button>
        <select
          v-if="projectList.length > 0"
          v-model="projectFilter"
          class="select select-bordered select-sm"
          aria-label="Filter by project"
          @change="filterSessions"
        >
          <option value="">All Projects</option>
          <option v-for="p in projectList" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search sessions..."
          class="input input-bordered input-sm w-64"
          data-testid="session-search-input"
          aria-label="Search sessions"
          @input="filterSessions"
        >
        <button
          @click="refreshSessions"
          class="btn btn-primary btn-sm"
          :disabled="loading"
          data-testid="refresh-sessions-btn"
        >
          <span v-if="loading" class="loading loading-spinner loading-xs" data-testid="refresh-spinner"></span>
          <span v-else>Refresh</span>
        </button>
      </div>
    </div>

    <!-- Page size lives on its own row under the refresh button, right-aligned. -->
    <div class="flex justify-end mb-3 -mt-2">
      <select
        v-model.number="pageSize"
        class="select select-bordered select-sm"
        data-testid="page-size-selector"
        aria-label="Page size"
        @change="filterSessions"
      >
        <option :value="10">10 / page</option>
        <option :value="25">25 / page</option>
        <option :value="50">50 / page</option>
        <option :value="100">100 / page</option>
      </select>
    </div>

      <div v-if="loading" class="flex justify-center py-8" data-testid="session-list-loading">
        <span class="loading loading-dots loading-lg" data-testid="loading-spinner"></span>
      </div>

      <div v-else-if="fetchError" class="alert alert-error" data-testid="fetch-error">
        <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>{{ fetchError }}</span>
      </div>

      <div v-else>
        <div v-if="sortedSessions.length === 0" class="text-center py-8 text-base-content/50" data-testid="no-sessions-message">
          No sessions found
        </div>

        <div v-else>
          <div class="overflow-x-auto">
            <table class="table table-zebra" data-testid="sessions-table" aria-label="Session List">
              <thead>
                <tr>
                  <th class="cursor-pointer hover:bg-base-200" data-testid="header-user" @click="sortSessions('userId')">
                    <div class="flex items-center gap-2">
                      User
                      <span class="text-xs opacity-50">
                        {{ sortColumn === 'userId' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}
                      </span>
                    </div>
                  </th>
                  <th class="cursor-pointer hover:bg-base-200" data-testid="header-url" @click="sortSessions('pageUrl')">
                    <div class="flex items-center gap-2">
                      URL
                      <span class="text-xs opacity-50">
                        {{ sortColumn === 'pageUrl' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}
                      </span>
                    </div>
                  </th>
                  <th class="cursor-pointer hover:bg-base-200" data-testid="header-start-time" @click="sortSessions('startTime')">
                    <div class="flex items-center gap-2">
                      Start Time
                      <span class="text-xs opacity-50">
                        {{ sortColumn === 'startTime' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}
                      </span>
                    </div>
                  </th>
                  <th class="cursor-pointer hover:bg-base-200" data-testid="header-duration" @click="sortSessions('durationMs')">
                    <div class="flex items-center gap-2">
                      Duration
                      <span class="text-xs opacity-50">
                        {{ sortColumn === 'durationMs' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}
                      </span>
                    </div>
                  </th>
                  <th class="cursor-pointer hover:bg-base-200" data-testid="header-events" @click="sortSessions('eventCount')">
                    <div class="flex items-center gap-2">
                      Events
                      <span class="text-xs opacity-50">
                        {{ sortColumn === 'eventCount' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}
                      </span>
                    </div>
                  </th>
                  <th data-testid="header-device" class="text-center">Device</th>
                  <th data-testid="header-location">Location</th>
                  <th class="cursor-pointer hover:bg-base-200" data-testid="header-status" @click="sortSessions('status')">
                    <div class="flex items-center gap-2">
                      Status
                      <span class="text-xs opacity-50">
                        {{ sortColumn === 'status' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}
                      </span>
                    </div>
                  </th>
                  <th class="cursor-pointer hover:bg-base-200" data-testid="header-size" @click="sortSessions('sizeBytes')">
                    <div class="flex items-center gap-2">
                      Size
                      <span class="text-xs opacity-50">
                        {{ sortColumn === 'sizeBytes' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}
                      </span>
                    </div>
                  </th>
                  <th data-testid="header-actions">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="sessions-tbody">
                <tr
                  v-for="session in paginatedSessions"
                  :key="session.sessionId"
                  class="group hover:bg-base-200/50 transition-colors cursor-pointer"
                  :data-testid="`session-row-${session.sessionId}`"
                  @click="selectSession(session)"
                >
                  <td class="text-sm">
                    <div v-if="session.userId" class="flex items-center gap-2">
                      <img
                        :src="avatarUrl(session.userId)"
                        :alt="session.userId"
                        class="w-7 h-7 rounded-full bg-base-200 shrink-0"
                        loading="lazy"
                        referrerpolicy="no-referrer"
                      />
                      <span class="truncate">{{ session.userId }}</span>
                    </div>
                    <span v-else>-</span>
                  </td>
                  <td class="text-sm max-w-xs truncate" :title="session.pageUrl">{{ hostFromUrl(session.pageUrl) }}</td>
                  <td class="text-sm">
                    <div class="flex flex-col">
                      <span class="font-medium">{{ formatRelativeTime(session.startTime) }}</span>
                      <span class="text-xs opacity-60">{{ formatDate(session.startTime) }}</span>
                    </div>
                  </td>
                  <td class="text-sm">{{ formatDuration(session.durationMs) }}</td>
                  <td class="text-sm">{{ session.eventCount || 0 }}</td>
                  <td class="text-center" :title="session.deviceType ?? 'unknown'">
                    <span class="text-base">{{ deviceIcon(session.deviceType) }}</span>
                  </td>
                  <td class="text-sm">
                    <span v-if="session.country" class="inline-flex items-center gap-1" :title="locationText(session)">
                      <span class="text-base">{{ countryFlag(session.country) }}</span>
                      <span class="truncate max-w-32">{{ session.city || session.countryName || session.country }}</span>
                    </span>
                    <span v-else class="text-base-content/40">—</span>
                  </td>
                  <td>
                    <div class="flex items-center gap-1">
                      <span class="badge badge-sm" :class="session.status === 'active' ? 'badge-success' : 'badge-ghost'">
                        {{ session.status }}
                      </span>
                      <span v-if="session.hasErrors" class="badge badge-error badge-xs" :title="`${session.errorCount} JS error(s)`">ERR</span>
                      <span v-if="session.frustrationScore && session.frustrationScore > 0" class="badge badge-warning badge-xs" :title="`Frustration: ${session.frustrationScore}`">F:{{ session.frustrationScore }}</span>
                    </div>
                  </td>
                  <td class="text-sm font-mono" :title="session.sizeBytes ? `${session.sizeBytes.toLocaleString()} bytes` : ''">
                    {{ formatBytes(session.sizeBytes) }}
                  </td>
                  <td>
                    <div class="flex items-center gap-1">
                      <button
                        @click.stop="toggleSessionFavorite(session)"
                        class="btn btn-ghost btn-xs btn-square"
                        :class="session.favoritedByMe ? 'text-warning' : 'text-base-content/40'"
                        data-testid="row-favorite-btn"
                        :title="session.favoritedByMe ? 'Unfavorite' : 'Favorite'"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4"
                          :fill="session.favoritedByMe ? 'currentColor' : 'none'"
                          viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      </button>
                      <button
                        @click.stop="selectSession(session)"
                        class="btn btn-info btn-xs"
                        data-testid="replay-btn"
                      >
                        Replay
                      </button>
                      <button
                        @click.stop="confirmDelete(session)"
                        class="btn btn-error btn-xs"
                        :disabled="deletingId === session.sessionId"
                        data-testid="delete-btn"
                        title="Delete session and all data"
                      >
                        <span v-if="deletingId === session.sessionId" class="loading loading-spinner loading-xs"></span>
                        <span v-else>Delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="flex items-center justify-between mt-4">
            <div class="text-sm text-base-content/60">
              Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, serverTotalCount) }} of {{ serverTotalCount }}
            </div>
            <div class="join">
              <button
                class="join-item btn btn-sm"
                :disabled="currentPage === 1"
                @click="goToPage(currentPage - 1)"
              >
                «
              </button>
              <button
                v-for="(page, index) in pageNumbers"
                :key="index"
                class="join-item btn btn-sm"
                :class="{ 'btn-active': page === currentPage }"
                :disabled="page === '...'"
                @click="typeof page === 'number' ? goToPage(page) : null"
              >
                {{ page }}
              </button>
              <button
                class="join-item btn btn-sm"
                :disabled="currentPage === totalPages"
                @click="goToPage(currentPage + 1)"
              >
                »
              </button>
            </div>
          </div>
        </div>
      </div>

    <ConfirmDialog
      :open="pendingDelete !== null"
      title="Delete session?"
      :message="pendingDelete
        ? `Delete session ${pendingDelete.sessionId} and all its events, devtools data, and attachments? This cannot be undone.`
        : ''"
      confirm-label="Delete"
      variant="danger"
      :busy="deletingId !== null"
      @confirm="performDelete"
      @cancel="cancelDelete"
    />

    <!-- Error picker modal — paginated list of distinct errors aggregated
         across sessions. Click a row to populate the "Error contains…"
         filter with the exact fingerprint and reload the session list. -->
    <div
      v-if="errorPickerOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="error-picker-modal"
      @click.self="errorPickerOpen = false"
    >
      <div class="bg-base-100 rounded-box shadow-2xl w-[min(720px,90vw)] max-h-[80vh] flex flex-col">
        <div class="px-5 py-3 border-b border-base-300 flex items-center justify-between">
          <div>
            <h3 class="font-semibold text-base">Browse captured errors</h3>
            <p class="text-xs text-base-content/60 mt-0.5">
              Click any row to filter sessions by that error.
            </p>
          </div>
          <button class="btn btn-sm btn-circle btn-ghost" @click="errorPickerOpen = false" aria-label="Close">✕</button>
        </div>
        <div class="px-5 py-2 border-b border-base-300">
          <input
            v-model="errorPickerQuery"
            type="text"
            class="input input-bordered input-sm w-full"
            placeholder="Type to narrow…"
            @keydown.enter="errorPickerPage = 1; loadErrorPickerPage()"
            @change="errorPickerPage = 1; loadErrorPickerPage()"
          />
        </div>
        <div class="flex-1 overflow-y-auto px-2">
          <div v-if="errorPickerLoading" class="p-6 text-center">
            <span class="loading loading-spinner"></span>
          </div>
          <div v-else-if="errorPickerError" class="p-6 text-center text-error text-sm">
            {{ errorPickerError }}
          </div>
          <div v-else-if="errorPickerItems.length === 0" class="p-6 text-center text-base-content/60 text-sm">
            No errors captured yet.
          </div>
          <ul v-else class="menu menu-sm p-0">
            <li v-for="opt in errorPickerItems" :key="opt.fingerprint">
              <a
                class="rounded-box flex items-start gap-3 py-2"
                @click="pickError(opt.fingerprint)"
              >
                <span class="badge badge-neutral badge-sm shrink-0">{{ opt.sessionCount }}</span>
                <span class="font-mono text-xs break-all">{{ opt.fingerprint }}</span>
              </a>
            </li>
          </ul>
        </div>
        <div class="px-5 py-3 border-t border-base-300 flex items-center justify-between text-xs">
          <span class="text-base-content/60">
            {{ errorPickerTotal }} distinct error{{ errorPickerTotal === 1 ? '' : 's' }}
          </span>
          <div class="flex items-center gap-2">
            <button
              class="btn btn-xs btn-ghost"
              :disabled="errorPickerPage <= 1 || errorPickerLoading"
              @click="changeErrorPickerPage(-1)"
            >← Prev</button>
            <span>
              Page {{ errorPickerPage }} / {{ Math.max(1, Math.ceil(errorPickerTotal / errorPickerPageSize)) }}
            </span>
            <button
              class="btn btn-xs btn-ghost"
              :disabled="errorPickerPage >= Math.ceil(errorPickerTotal / errorPickerPageSize) || errorPickerLoading"
              @click="changeErrorPickerPage(1)"
            >Next →</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom-event tag picker modal — same shape as the error picker.
         Click a row to populate the "Custom event…" filter and reload. -->
    <div
      v-if="tagPickerOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="custom-event-picker-modal"
      @click.self="tagPickerOpen = false"
    >
      <div class="bg-base-100 rounded-box shadow-2xl w-[min(720px,90vw)] max-h-[80vh] flex flex-col">
        <div class="px-5 py-3 border-b border-base-300 flex items-center justify-between">
          <div>
            <h3 class="font-semibold text-base">Browse custom events</h3>
            <p class="text-xs text-base-content/60 mt-0.5">
              Click any tag to filter sessions that fired it.
            </p>
          </div>
          <button class="btn btn-sm btn-circle btn-ghost" @click="tagPickerOpen = false" aria-label="Close">✕</button>
        </div>
        <div class="px-5 py-2 border-b border-base-300">
          <input
            v-model="tagPickerQuery"
            type="text"
            class="input input-bordered input-sm w-full"
            placeholder="Type to narrow…"
            @keydown.enter="tagPickerPage = 1; loadTagPickerPage()"
            @change="tagPickerPage = 1; loadTagPickerPage()"
          />
        </div>
        <div class="flex-1 overflow-y-auto px-2">
          <div v-if="tagPickerLoading" class="p-6 text-center">
            <span class="loading loading-spinner"></span>
          </div>
          <div v-else-if="tagPickerError" class="p-6 text-center text-error text-sm">
            {{ tagPickerError }}
          </div>
          <div v-else-if="tagPickerItems.length === 0" class="p-6 text-center text-base-content/60 text-sm">
            No custom events captured yet.
          </div>
          <ul v-else class="menu menu-sm p-0">
            <li v-for="opt in tagPickerItems" :key="opt.tag">
              <a
                class="rounded-box flex items-start gap-3 py-2"
                @click="pickTag(opt.tag)"
              >
                <span class="badge badge-neutral badge-sm shrink-0">{{ opt.sessionCount }}</span>
                <span class="font-mono text-xs break-all">{{ opt.tag }}</span>
              </a>
            </li>
          </ul>
        </div>
        <div class="px-5 py-3 border-t border-base-300 flex items-center justify-between text-xs">
          <span class="text-base-content/60">
            {{ tagPickerTotal }} distinct tag{{ tagPickerTotal === 1 ? '' : 's' }}
          </span>
          <div class="flex items-center gap-2">
            <button
              class="btn btn-xs btn-ghost"
              :disabled="tagPickerPage <= 1 || tagPickerLoading"
              @click="changeTagPickerPage(-1)"
            >← Prev</button>
            <span>
              Page {{ tagPickerPage }} / {{ Math.max(1, Math.ceil(tagPickerTotal / tagPickerPageSize)) }}
            </span>
            <button
              class="btn btn-xs btn-ghost"
              :disabled="tagPickerPage >= Math.ceil(tagPickerTotal / tagPickerPageSize) || tagPickerLoading"
              @click="changeTagPickerPage(1)"
            >Next →</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
